import React, {
    useState,
    useEffect,
    useImperativeHandle,
    forwardRef,
    useRef,
    useMemo,
} from "react";
import {
    IFilterParams,
    IFilterComp,
    IDoesFilterPassParams,
} from "ag-grid-community";
import { useSelector } from "react-redux";
import {
    IChannel,
    TActiveClientId,
    TClientTags,
    TagsMap,
} from "@/ts/common-types";
import { filter, map } from "underscore";
import { Switch } from "antd";
import { getActiveModule } from "@/ts/utils";
import { TAGS_MODULE_MAPPING } from "@/ts/constants";

// Define the types for the custom filter props and model
interface CustomSetFilterModel {
    values: string[];
    exclude: boolean;
    filterType: string;
}

interface CustomSetFilterProps extends IFilterParams {
    values: string[];
    tagMappingField: string;
}

// Custom filter component
const CustomTagTypeFilter = forwardRef<IFilterComp, CustomSetFilterProps>(
    (props, ref) => {
        const { activeModule } = getActiveModule();
        const activeClientId = useSelector(
            (state: TActiveClientId) => state.activeClientId.value
        );
        const channel = useSelector((state: IChannel) => state.channel.value);

        const tagTypeList = useSelector(
            (state: { tagTypeList: { value: TClientTags[] } }) =>
                state.tagTypeList.value
        );

        const { recordType } =
            TAGS_MODULE_MAPPING[activeModule as keyof TagsMap];

        const recordTagTypes = useMemo(
            () =>
                map(
                    filter(tagTypeList, (tagType) =>
                        ["global", recordType].includes(
                            tagType.record_type.toLowerCase()
                        )
                    ),
                    (type) => type.tag_type
                ),
            [tagTypeList, recordType]
        );

        const [isFilterRemoved, setIsFilterRemoved] = useState(false);
        const [isButtonClicked, setIsButtonClicked] = useState(false);
        const [isExcludeMode, setIsExcludeMode] = useState(false);
        const [selectedValues, setSelectedValues] = useState<string[]>([]);
        const guiRef = useRef<HTMLDivElement>(null);

        // Toggle between "IN" and "NOT IN"
        const onExcludeToggle = () => {
            setIsExcludeMode(!isExcludeMode);
        };

        // Handle value selection changes
        const onValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            const newSelectedValues = [...selectedValues];
            if (newSelectedValues.includes(value)) {
                const index = newSelectedValues.indexOf(value);
                newSelectedValues.splice(index, 1);
            } else {
                newSelectedValues.push(value);
            }
            setSelectedValues(newSelectedValues);
        };

        const onApply = () => {
            setSelectedValues(selectedValues);
            setIsExcludeMode(isExcludeMode);
            setIsButtonClicked(true);
        };

        const onReset = () => {
            setSelectedValues([]);
            setIsExcludeMode(false);
            setIsButtonClicked(true);
        };

        useEffect(() => {
            if (isButtonClicked) {
                props.filterChangedCallback();
                setIsButtonClicked(false);
            }
        }, [
            selectedValues,
            isExcludeMode,
            activeClientId,
            channel,
            isButtonClicked,
        ]);

        // Implement the filter logic
        const doesFilterPass = (params: IDoesFilterPassParams) => {
            const cellValue = map(params.data[props.tagMappingField], (item: Partial<TClientTags>) => item.tag_type);
            const filterResult = selectedValues.some((value) =>
                cellValue.includes(value)
            );
            return isExcludeMode ? !filterResult : filterResult;
        };

        // Return model for saving filter state
        const getModel = (): CustomSetFilterModel | null => {
            if (selectedValues.length === 0) {
                return null;
            }
            return {
                // similar to the default set filter - have added an extra param to identify if filter is to be excluded
                filterType: "set",
                values: selectedValues,
                exclude: isExcludeMode,
            };
        };

        // Restore filter state
        const setModel = (model: CustomSetFilterModel | null): void => {
            if (!model) {
                // If no model is provided, reset the filter state (filter remove)
                setSelectedValues([]);
                setIsExcludeMode(false);
                setIsFilterRemoved(true);
            } else {
                // Restore the model (filter applied)
                setSelectedValues(model.values || []);
                setIsExcludeMode(model.exclude || false);
                setIsFilterRemoved(false);
            }
        };

        useEffect(() => {
            // Refresh the custom filter if it was applied when reset filters was called
            if (isFilterRemoved) {
                props.filterChangedCallback();
                setIsFilterRemoved(false);
            }
        }, [isFilterRemoved]);

        // Expose AG Grid filter lifecycle methods
        useImperativeHandle(ref, () => ({
            doesFilterPass,
            isFilterActive: () => selectedValues.length > 0,
            getModel,
            setModel,
            getGui: () => guiRef.current!,
        }));

        return (
            <div ref={guiRef} className="custom-filter-container">
                <div className="mb-3 d-flex align-items-center justify-content-left">
                    <Switch
                        size="small"
                        className="me-1"
                        checked={isExcludeMode}
                        onChange={onExcludeToggle}
                    />
                    <label>Exclude Mode</label>
                </div>

                <div>
                    {map(recordTagTypes, (value) => (
                        <div
                            className="custom-checkbox mb-1 d-flex align-items-center"
                            key={value}
                        >
                            <input
                                type="checkbox"
                                value={value}
                                onChange={onValueChange}
                                checked={selectedValues.includes(value)}
                                style={{
                                    width: "16px",
                                    height: "16px",
                                    marginRight: "5px",
                                }}
                            />
                            {value}
                        </div>
                    ))}
                </div>
                <div className="border mt-2"></div>
                <div className="custom-filter-buttons">
                    <button
                        className="ag-standard-button ag-filter-apply-panel-button"
                        onClick={onReset}
                    >
                        Reset
                    </button>
                    <button
                        className="ag-standard-button ag-filter-apply-panel-button"
                        onClick={onApply}
                    >
                        Apply
                    </button>
                </div>
            </div>
        );
    }
);

export default CustomTagTypeFilter;
